import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ImportAPI } from '../services/api/import.api';
import { ImportAPIFeil } from '../services/api/import.api';
import type { ImportFeilRad, ImportForhandsvisning, ImportKolonne } from '../services/api/types';
import { lesRegneark } from '../components/common/import/lesRegneark';
import { lagCsv } from '../components/common/import/lagCsv';

export type ImportSteg = 'velg' | 'forhandsvis' | 'importerer' | 'ferdig';

const FEILTEKST: Record<number, string> = {
  401: 'Du er logget ut. Logg inn på nytt.',
  403: 'Du har ikke tilgang til å importere.',
  429: 'For mange forsøk. Vent litt og prøv igjen.',
};
const UKJENT = 'Noe gikk galt. Prøv igjen.';

const tilFeil = (err: unknown): ImportFeilRad[] => {
  if (err instanceof ImportAPIFeil) return err.feil;
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return [{ melding: (status !== undefined && FEILTEKST[status]) || UKJENT }];
};

export type LesFilerOpsjoner = { maksFilstorrelse?: number };
export type LesFilerResultat = { csv: string; filadvarsler?: string[] } | { feil: ImportFeilRad[] };
export type LesFiler = (filer: File[], opsjoner: LesFilerOpsjoner) => Promise<LesFilerResultat>;
export interface ForhandsvisOpsjoner {
  /**
   * Ny forhåndsvisning etter en rettelse: den gamle visningen og utvalget står mens den nye hentes.
   * Feiler den nye, står den gamle visningen igjen, men importen er stoppet (den mangler rettelsen).
   */
  behold?: boolean;
  /** Utvalg etter ny forhåndsvisning: velgbare radnr i svaret og forrige utvalg. Standard er alle velgbare. */
  valg?: (velgbare: number[], forrige: ReadonlySet<number>) => Set<number>;
}

export const useImport = <R, Res>(
  api: ImportAPI<R, Res>,
  kolonner: ImportKolonne[],
  {
    maksRader,
    maksFilstorrelse,
    erVelgbar,
    forbigaendeFeilKanProvesIgjen = false,
  }: {
    maksRader?: number;
    maksFilstorrelse?: number;
    /** Rader som gir false står utenfor utvalget (aldri valgt eller sendt); standard er alle uten feil. */
    erVelgbar?: (rad: ImportForhandsvisning<R>['rader'][number]) => boolean;
    /**
     * Standard (false): enhver feil uten radnr fra bekreft låser importen.
     * True: bare filfeil fra serveren (ImportAPIFeil uten radnr) låser; nettfeil, 401/403/429 og 5xx kan prøves på nytt.
     */
    forbigaendeFeilKanProvesIgjen?: boolean;
  } = {}
) => {
  const [steg, setSteg] = useState<ImportSteg>('velg');
  const [laster, setLaster] = useState(false);
  const [forhandsvisning, setForhandsvisning] = useState<ImportForhandsvisning<R> | null>(null);
  const [feil, setFeil] = useState<ImportFeilRad[]>([]);
  const [resultat, setResultat] = useState<Res | null>(null);
  const [filnavn, setFilnavn] = useState('');
  // Satt når bekreft ble avvist med en filfeil som ikke kan rettes ved nytt forsøk.
  const [laast, setLaast] = useState(false);
  // Satt når en ny forhåndsvisning (behold) feilet: visningen mangler siste rettelse og kan ikke importeres.
  const [utdatert, setUtdatert] = useState(false);
  // Advarsler om hele filer (uten radnr), bare fra sangfilimport.
  const [filadvarsler, setFiladvarsler] = useState<string[]>([]);
  // Valgte radnr. Bare rader uten feil kan velges; bekreft sender kun disse som ny CSV.
  const [valgte, setValgte] = useState<Set<number>>(new Set());
  // Økes ved tilbakestill/unmount slik at svar fra gamle forespørsler ignoreres.
  const generasjon = useRef(0);
  // Synkron lås: to bekreft-kall i samme batch skal gi ett API-kall.
  const bekrefter = useRef(false);

  // Rader med feil fra forhåndsvisningen kan aldri velges.
  const feilRadnr = useMemo(
    () => new Set((forhandsvisning?.feil ?? []).flatMap((f) => (f.radnr === undefined ? [] : [f.radnr]))),
    [forhandsvisning]
  );
  const velgbare = useMemo(
    () =>
      (forhandsvisning?.rader ?? [])
        .filter((r) => !feilRadnr.has(r.radnr) && (erVelgbar?.(r) ?? true))
        .map((r) => r.radnr),
    [forhandsvisning, feilRadnr, erVelgbar]
  );

  const veksleRad = useCallback(
    (radnr: number) => {
      if (!velgbare.includes(radnr)) return;
      setValgte((forrige) => {
        const neste = new Set(forrige);
        if (!neste.delete(radnr)) neste.add(radnr);
        return neste;
      });
    },
    [velgbare]
  );

  const veksleAlle = useCallback(() => {
    setValgte((forrige) => (velgbare.length > 0 && forrige.size === velgbare.length ? new Set() : new Set(velgbare)));
  }, [velgbare]);

  useEffect(() => {
    return () => {
      generasjon.current += 1;
    };
  }, []);

  // Felles flyt: les til CSV, la serveren forhåndsvise, sett utvalget.
  const kjorForhandsvisning = useCallback(
    async (navn: string, les: () => Promise<LesFilerResultat>, { behold = false, valg }: ForhandsvisOpsjoner = {}) => {
      const mi = ++generasjon.current;
      setLaster(true);
      if (!behold) {
        setFeil([]);
        setLaast(false);
        setUtdatert(false);
        setForhandsvisning(null);
        setFilnavn(navn);
        setFiladvarsler([]);
        setValgte(new Set());
      }
      const feilet = (nyeFeil: ImportFeilRad[], laas: boolean) => {
        setFeil(nyeFeil);
        if (behold) {
          setUtdatert(true);
          setLaast(laas);
          setSteg('forhandsvis');
        } else {
          setSteg('velg');
        }
      };
      try {
        const lest = await les();
        if (mi !== generasjon.current) return;
        if ('feil' in lest) {
          feilet(lest.feil, false);
          return;
        }
        const svar = await api.forhandsvis(lest.csv, navn);
        if (mi !== generasjon.current) return;
        const svarFeilRadnr = new Set((svar.feil ?? []).flatMap((f) => (f.radnr === undefined ? [] : [f.radnr])));
        const velgbareSvar = svar.rader.filter((r) => !svarFeilRadnr.has(r.radnr) && (erVelgbar?.(r) ?? true)).map((r) => r.radnr);
        if (behold && valg) setValgte((forrige) => valg(velgbareSvar, forrige));
        else setValgte(new Set(velgbareSvar));
        setForhandsvisning(svar);
        setFilnavn(navn);
        setFiladvarsler(('filadvarsler' in lest && lest.filadvarsler) || []);
        setFeil(svar.feil ?? []);
        setLaast(false);
        setUtdatert(false);
        setSteg('forhandsvis');
      } catch (err) {
        if (mi !== generasjon.current) return;
        feilet(tilFeil(err), err instanceof ImportAPIFeil && tilFeil(err).some((f) => f.radnr === undefined));
      } finally {
        if (mi === generasjon.current) setLaster(false);
      }
    },
    [api, erVelgbar]
  );

  const velgFil = useCallback(
    (fil: File) => kjorForhandsvisning(fil.name, () => lesRegneark(fil, { maksRader, maksFilstorrelse })),
    [kjorForhandsvisning, maksRader, maksFilstorrelse]
  );

  const velgFiler = useCallback(
    (filer: File[], lesFiler: LesFiler, opsjoner?: ForhandsvisOpsjoner) =>
      kjorForhandsvisning(`${filer.length} sangfiler`, () => lesFiler(filer, { maksFilstorrelse }), opsjoner),
    [kjorForhandsvisning, maksFilstorrelse]
  );

  const bekreft = useCallback(async (): Promise<Res | null> => {
    const stoppet = utdatert || (forbigaendeFeilKanProvesIgjen ? laast || (forhandsvisning?.feil ?? []).some((f) => f.radnr === undefined) : feil.some((f) => f.radnr === undefined));
    if (!forhandsvisning || stoppet || bekrefter.current) return null;
    const utvalg = forhandsvisning.rader.filter((r) => valgte.has(r.radnr) && !feilRadnr.has(r.radnr) && (erVelgbar?.(r) ?? true));
    if (utvalg.length === 0) return null;
    // Nettleseren sender et utvalg av radene som ny CSV; serveren validerer den som enhver annen fil.
    const csv = lagCsv(
      kolonner.map((k) => k.overskrift),
      utvalg.map((rad) => kolonner.map((k) => String((rad as Record<string, unknown>)[k.felt] ?? '')))
    );
    bekrefter.current = true;
    const mi = ++generasjon.current;
    setSteg('importerer');
    setLaster(true);
    try {
      const svar = await api.bekreft(csv, filnavn);
      if (mi !== generasjon.current) return svar;
      setResultat(svar);
      setSteg('ferdig');
      return svar;
    } catch (err) {
      if (mi !== generasjon.current) return null;
      const nyeFeil = tilFeil(err);
      setFeil(nyeFeil);
      setLaast(err instanceof ImportAPIFeil && nyeFeil.some((f) => f.radnr === undefined));
      setSteg('forhandsvis');
      return null;
    } finally {
      bekrefter.current = false;
      if (mi === generasjon.current) setLaster(false);
    }
  }, [api, forhandsvisning, feil, filnavn, valgte, feilRadnr, kolonner, erVelgbar, forbigaendeFeilKanProvesIgjen, laast, utdatert]);

  const tilbakestill = useCallback(() => {
    generasjon.current += 1;
    bekrefter.current = false;
    setSteg('velg');
    setLaster(false);
    setForhandsvisning(null);
    setFeil([]);
    setLaast(false);
    setUtdatert(false);
    setResultat(null);
    setFilnavn('');
    setFiladvarsler([]);
    setValgte(new Set());
  }, []);

  return {
    steg,
    laster,
    forhandsvisning,
    feil,
    laast: laast || utdatert || (forhandsvisning?.feil ?? []).some((f) => f.radnr === undefined),
    resultat,
    valgte,
    velgbare,
    feilRadnr,
    filadvarsler,
    velgFil,
    velgFiler,
    bekreft,
    veksleRad,
    veksleAlle,
    tilbakestill,
  };
};
